import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  FileText,
  Lightbulb,
  Loader,
  PanelRightClose,
  PanelRightOpen,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import ApplicantChatView, { type ThreadToolsApi } from '../ApplicantChatView';
import CannedReplyPicker from './CannedReplyPicker';
import ImproveTextDropdown from '../shared/ImproveTextDropdown';
import { auth } from '../../lib/firebase';
import { workspaceHeader } from '../../lib/workspaces';
import type { Ticket } from '../../types';

/**
 * Admin ticket thread — Chat Phase 3 convergence.
 *
 * The tickets workspace's middle column used to embed the standalone
 * TicketDetailModal; it now mounts the shared chat engine
 * (ApplicantChatView) with the typed `admin` participant, exactly like
 * every other role's thread. Staff-only capabilities that are not thread
 * primitives — AI assist, canned replies, text improvement, the context
 * rail toggle — enter through the engine's header/composer tool slots.
 * The engine itself carries the staff thread features (internal notes,
 * conversation/internal/documents tabs, reactions, audit chips, drafts).
 *
 * The context rail (TicketSplitView) stays the home of assignment,
 * status/priority editing and the timeline; the host keeps stamping the
 * server's ticket read state when a ticket is opened.
 */

interface AdminTicketThreadProps {
  ticket: Ticket;
  /** Back / close the thread (mobile drill-in). */
  onClose: () => void;
  /** Merge-style host update after thread-level mutations (title edit, reopen). */
  onTicketChanged: (ticketId: string, updates: Partial<Ticket>) => void;
  applicantPhotoUrl?: string;
  detailsOpen?: boolean;
  onToggleDetails?: () => void;
}

type TicketAIMode = 'summarize' | 'suggest_next' | 'draft_reply';

export default function AdminTicketThread({
  ticket,
  onClose,
  onTicketChanged,
  applicantPhotoUrl,
  detailsOpen,
  onToggleDetails,
}: AdminTicketThreadProps) {
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [aiMode, setAiMode] = useState<TicketAIMode>('summarize');
  const [aiResult, setAiResult] = useState('');
  const [aiError, setAiError] = useState('');
  const aiMenuRef = useRef<HTMLDivElement>(null);
  const aiApiRef = useRef<ThreadToolsApi | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(e.target as Node)) setAiMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  /* Same endpoint + payload the standalone thread used. The api handed to
     the header slot carries the live ticket and the full comment stream
     (internal notes included) for context. */
  const runTicketAI = useCallback(async (mode: TicketAIMode) => {
    const api = aiApiRef.current;
    if (!api?.ticket) return;
    setAiMode(mode);
    setAiOpen(true);
    setAiResult('');
    setAiError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!auth.currentUser) throw new Error('Not signed in.');
      const res = await fetch('/api/tickets/ai-assist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...workspaceHeader(),
        },
        body: JSON.stringify({ action: mode, ticket: api.ticket, comments: api.comments }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'AI assist failed.');
      const result = typeof data.result === 'string' ? data.result : '';
      if (mode === 'draft_reply' && result) {
        api.appendToComposer(result);
        setAiOpen(false);
      } else {
        setAiResult(result);
      }
    } catch (err: any) {
      setAiError(err?.message || 'AI assist failed.');
    }
  }, []);

  const headerTools = useCallback(
    (api: ThreadToolsApi) => {
      aiApiRef.current = api;
      if (!api.isStaffAdmin) return null;
      return (
        <>
          <div className="relative" ref={aiMenuRef}>
            <button
              type="button"
              onClick={() => setAiMenuOpen((v) => !v)}
              className="flex items-center gap-1.5 rounded-full bg-violet-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-violet-700 transition-colors"
              aria-haspopup="menu"
              aria-expanded={aiMenuOpen}
              title="AI tools for this ticket"
            >
              <Sparkles size={13} /> AI Assist
            </button>
            {aiMenuOpen && (
              <div className="absolute right-0 top-full z-30 mt-2 w-60 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-start gap-2.5 px-3.5 py-3 text-left hover:bg-violet-50"
                  onClick={() => { setAiMenuOpen(false); void runTicketAI('draft_reply'); }}
                >
                  <Send size={15} className="mt-0.5 text-violet-500" />
                  <span>
                    <span className="block text-[13px] font-semibold text-gray-800">Draft a reply</span>
                    <span className="block text-[11px] text-gray-500">Insert a suggested reply into the composer</span>
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-start gap-2.5 px-3.5 py-3 text-left hover:bg-violet-50"
                  onClick={() => { setAiMenuOpen(false); void runTicketAI('summarize'); }}
                >
                  <FileText size={15} className="mt-0.5 text-violet-500" />
                  <span>
                    <span className="block text-[13px] font-semibold text-gray-800">Summarize ticket</span>
                    <span className="block text-[11px] text-gray-500">Get a quick brief of the conversation so far</span>
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-start gap-2.5 px-3.5 py-3 text-left hover:bg-violet-50"
                  onClick={() => { setAiMenuOpen(false); void runTicketAI('suggest_next'); }}
                >
                  <Lightbulb size={15} className="mt-0.5 text-violet-500" />
                  <span>
                    <span className="block text-[13px] font-semibold text-gray-800">Suggest next steps</span>
                    <span className="block text-[11px] text-gray-500">Recommend what to do next on this ticket</span>
                  </span>
                </button>
              </div>
            )}
          </div>
          {onToggleDetails && (
            <button
              type="button"
              onClick={onToggleDetails}
              className={`hidden md:flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${detailsOpen ? 'bg-indigo-50 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'}`}
              title={detailsOpen ? 'Hide ticket details' : 'Show ticket details'}
            >
              {detailsOpen ? <PanelRightClose size={15} /> : <PanelRightOpen size={15} />}
              Details
            </button>
          )}
        </>
      );
    },
    [detailsOpen, onToggleDetails, aiMenuOpen, runTicketAI]
  );

  const composerTools = useCallback((api: ThreadToolsApi) => {
    if (!api.isStaffAdmin) return null;
    return (
      <>
        <ImproveTextDropdown
          getValue={api.getComposerText}
          onImprove={api.setComposerText}
          isHtml={false}
          context="ticket comment"
        />
        <CannedReplyPicker onSelect={(text) => api.appendToComposer(text)} />
      </>
    );
  }, []);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {(aiResult || aiError) && aiOpen && (
        <div className="border-b border-violet-100 bg-gradient-to-r from-violet-50 to-fuchsia-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
              {aiError ? <AlertTriangle size={15} /> : <Sparkles size={15} />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold uppercase tracking-wider text-violet-500">
                {aiMode === 'summarize' ? 'Ticket Summary' : aiMode === 'suggest_next' ? 'Suggested Next Steps' : 'AI Draft'}
              </p>
              {aiError ? (
                <p className="mt-1 text-[13px] text-red-600">{aiError}</p>
              ) : aiResult ? (
                <p className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed text-gray-700">{aiResult}</p>
              ) : (
                <p className="mt-1 flex items-center gap-2 text-[13px] text-gray-500">
                  <Loader size={13} className="animate-spin" /> Thinking…
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setAiOpen(false); setAiResult(''); setAiError(''); }}
              className="rounded-md p-1 text-gray-400 hover:bg-white hover:text-gray-600"
              aria-label="Dismiss AI result"
            >
              <X size={15} />
            </button>
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1">
        <ApplicantChatView
          ticket={ticket}
          userId={ticket.applicantId}
          participant={{ kind: 'admin', id: auth.currentUser?.uid || '' }}
          mode="split"
          embedded
          onClose={onClose}
          onTicketUpdated={onTicketChanged}
          applicantPhotoUrl={applicantPhotoUrl}
          headerTools={headerTools}
          composerTools={composerTools}
        />
      </div>
    </div>
  );
}
